import INTOUCH_API from "../../services/Api";
import { useCallback, useEffect, useState } from "react";
import useCookie from "../../hooks/useCookie";
import useCustomNavigate from "../../hooks/useCustomNavigate";
import useAuth from "../../hooks/useAuth";
import useValidateForm from "../../hooks/useValidateForm";
import useBrowserStorage from "../../hooks/useBrowserStorage";
import { eighteenYearAgo } from "../../utils/helpers";
import Game from "../../store/Game";
import useCurrentGame from "../../hooks/useCurrentGame";
import useCurrentUserData from "../../hooks/useCurrentUserData";
import useEmailCodeConfirm from "../../hooks/useEmailCodeConfirm";
import { useTranslation } from "react-i18next";
import ROUTES from "../../utils/routes.js";
import {
  CURRENT_TEMP_DEMO_GENDER,
  LOYALTY_REF_CODE_KEY,
} from "../../utils/constants";

export default function useController(onSetNextStep) {
  const {
    getValidGenders,
    onComparableTextHandle,
    checkConsistency,
    validateBirthday,
    isValidEmail,
    isValidPassword,
    isConsistency,
    comparableText,
    isBirthdayValid,
    formatDate,
    isValidGenders,
    manageValidate,
  } = useValidateForm();

  const [isRegistrationLoading, setIsRegistrationLoading] = useState(false);
  const [responseServerErr, setResponseServerErr] = useState("");
  const [link, setLink] = useState("");
  const { setGame } = useCurrentGame();
  const { getCookie, removeCookie } = useCookie();
  const { setToken } = useAuth();
  const { setUserData } = useCurrentUserData();
  const { navigateTo } = useCustomNavigate();
  const { getItem, removeItem } = useBrowserStorage();
  const { confirmEmail } = useEmailCodeConfirm();
  const { t } = useTranslation();

  const [registerFormData, setRegisterFormData] = useState({
    firstName: "",
    birthday: formatDate(eighteenYearAgo),
    email: "",
    password: "",
    gender: "",
    buyLater: 1,
  });

  const handleChange = useCallback((e) => {
    const name = e.target.name;
    const value = e.target.value;
    if (name === "email" || name === "password") {
      manageValidate(name, value);
    }
    setRegisterFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const onBirthdayHandle = useCallback(
    (value) => {
      setRegisterFormData((prevState) => ({
        ...prevState,
        birthday: formatDate(value),
      }));
      validateBirthday(formatDate(value));
    },
    [registerFormData.birthday]
  );
  const onSubmitForm = async (event) => {
    event.preventDefault();
    if (isRegistrationLoading) return;
    if (!isRegisterFormValid()) return;
    // копия: дату не переписываем в состоянии формы (её можно отправить повторно)
    const formData = { ...registerFormData };
    formData.email = formData.email.trim().toLowerCase();
    formData.birthday = formData.birthday.split(".").reverse().join("-");
    // 23.10: сначала код на почту, аккаунт создаётся после его ввода (EmailCodeModal)
    startLoadRegistration();
    const err = await confirmEmail(formData.email, (emailCode) =>
      registerUser({ ...formData, emailCode })
    );
    stopLoadRegistration();
    if (err) {
      setResponseServerErr(err);
      setTimeout(() => setResponseServerErr(""), 5000);
    }
  };

  const isRegisterFormValid = () => {
    return (
      isValidGenders &&
      isValidEmail &&
      isValidPassword &&
      isConsistency &&
      isBirthdayValid.validAge &&
      isBirthdayValid.validFormat &&
      isFormFilled()
    );
  };

  const isFormFilled = () => {
    return (
      registerFormData.firstName.length &&
      registerFormData.password.length &&
      registerFormData.email.length &&
      registerFormData.gender.length &&
      registerFormData.birthday.length
    );
  };

  const detectGender = useCallback(() => {
    const gender = getCookie("inviterGender");
    if (!gender) {
      const genderDemo = JSON.parse(
        localStorage.getItem(CURRENT_TEMP_DEMO_GENDER)
      );
      if (!genderDemo) return;
      setRegisterFormData({
        ...registerFormData,
        gender: genderDemo,
      });
    } else {
      setRegisterFormData({
        ...registerFormData,
        gender: gender === "male" ? "female" : "male",
      });
    }
  }, [registerFormData.gender]);

  // возвращает текст ошибки или null: ошибку показывает окно ввода кода
  const registerUser = async (formData) => {
    let errorMessage = null;
    const invite_link = getCookie("invite_link");
    const errorLink = getCookie("errorLink");
    const ref = getItem("ref", "session");
    // per-user реф-код лояльности (ловится в App.js из ?ref=)
    const loyaltyRefCode = localStorage.getItem(LOYALTY_REF_CODE_KEY);
    if (loyaltyRefCode) {
      formData = { ...formData, refCode: loyaltyRefCode };
    }

    await INTOUCH_API.requestManager(
      "register",
      { formData, ref },
      async (response) => {
        setToken(response.data.token);
        localStorage.removeItem(LOYALTY_REF_CODE_KEY);
        removeCookie("inviterGender");
        removeItem("ref", "session");

        removeCookie("errorLink");

        if (invite_link && errorLink !== "isError") {
          navigateTo(`${ROUTES.LANDING_PAGE}?link=${invite_link}`);
          removeCookie("invite_link");
          return;
        }

        setGame(response.data.game);
        setUserData(response.data.user);
        Game.setGame(response.data.game);
        setLink(response.data.link.link);
        onSetNextStep && onSetNextStep();
      },
      (error) => {
        errorMessage =
          error.response?.data?.message || t("email_code.register_error");
      }
    );
    return errorMessage;
  };

  const startLoadRegistration = () => {
    setIsRegistrationLoading(true);
  };

  const stopLoadRegistration = () => {
    setIsRegistrationLoading(false);
  };

  useEffect(() => {
    setRegisterFormData({
      ...registerFormData,
      birthday: formatDate(eighteenYearAgo),
    });
    detectGender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkConsistency(registerFormData.password, comparableText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerFormData.password, comparableText]);

  useEffect(() => {
    return () => {
      setIsRegistrationLoading(null);
    };
  }, []);

  const onLinkCopyHandle = () => {};

  return {
    link,
    isRegistrationLoading,
    responseServerErr,
    onSubmitForm,
    comparableText,
    startLoadRegistration,
    stopLoadRegistration,
    registerFormData,
    onComparableTextHandle,
    handleChange,
    isRegisterFormValid,
    getValidGenders,
    onBirthdayHandle,
    isBirthdayValid,
    isValidEmail,
    isValidPassword,
    isConsistency,
  };
}
